import React from 'react';
import { ROUTES } from './ManageNotifications.config';
import { MainLayout } from '../../components/MainLayout';
import { PagesHeader } from '../../components/shared/PagesHeader';
import { InputSwitch } from 'primereact/inputswitch';
import './ManageNotifications.css';
import { Button } from 'primereact/button';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { FormikField } from '../../components/FormikField';
import { InputField, PhoneInputField } from '../../components/FormikFields';
import cn from 'classnames';
import { BlockUI } from 'primereact/blockui';

const ManageNotifications = ({
  pointsOfContact,
  onPointOfContactAdd,
  toggleEmail,
  togglePhoneNumber,
  onPointOfContactRemove,
  loading,
  addingContact,
  setAddingContact,
  updatedContacts,
  onSave,
  prefsUpdated,
}) => {
  const { t } = useTranslation();

  return (
    <MainLayout eInvoicing className={'manage-notifications'}>
      <PagesHeader
        title={t('manage-notifications.title')}
        titleClassName={'mb-3'}
        withBreadcrumb={true}
        routes={ROUTES}
      />

      <p className="description">{t('manage-notifications.description')}</p>
      <p className="description">
        <ol>
          {t('manage-notifications.description_points', {
            returnObjects: true,
          }).map((point) => (
            <li key={point.title}>
              <b>{point.title} </b>
              {point.description}
            </li>
          ))}
        </ol>
      </p>

      <BlockUI
        blocked={loading}
        template={
          <i
            className="pi pi-spin pi-spinner"
            style={{ color: 'var(--color-theme-primary)', fontSize: '3rem' }}
          />
        }
      >
        <div className="email-notifications">
          {pointsOfContact?.map((pointOfContact, i) => {
            const contactLabel =
              i === 0
                ? t('manage-notifications.primary-point-of-contact')
                : i === 1
                ? t('manage-notifications.secondary-point-of-contact')
                : t('manage-notifications.additional-point-of-contact');
            return (
              <div key={pointOfContact.contactId} className="point-of-contact">
                <p className="title">
                  {contactLabel}{' '}
                  {pointOfContact.status !== 'ACTIVE' && (
                    <small className={pointOfContact.status}>
                      {t(
                        'manage-notifications.status.' + pointOfContact.status,
                      )}
                    </small>
                  )}
                  {pointOfContact.status === 'EXPIRED' && (
                    <Button label="Resend" text link onClick={() => {}} />
                  )}
                  {!pointOfContact.isPrimary && (
                    <Button
                      icon={'pi pi-trash'}
                      text
                      link
                      onClick={() => {
                        onPointOfContactRemove(pointOfContact, i);
                      }}
                    />
                  )}
                </p>

                {i === 0 && (
                  <p className="subtitle">
                    {t('manage-notifications.enable-optional-notifications')}
                  </p>
                )}
                <p className="email">{pointOfContact.contactValueMail}</p>
                <InputSwitch
                  className="emailToggle"
                  checked={updatedContacts?.[i]?.channelMail}
                  onChange={() => toggleEmail(i)}
                />
                <p className="phone">{pointOfContact.contactValueSms}</p>
                <InputSwitch
                  className="phoneToggle"
                  checked={updatedContacts?.[i]?.channelSms}
                  onChange={() => togglePhoneNumber(i)}
                />
              </div>
            );
          })}
          {!addingContact && (
            <Button
              label={t('manage-notifications.add-additional-point-of-contact')}
              icon="pi pi-plus-circle"
              onClick={() => {
                setAddingContact(true);
              }}
            />
          )}

          {addingContact && (
            <div className="point-of-contact">
              <p className="title">
                {pointsOfContact.length === 1
                  ? t('manage-notifications.secondary-point-of-contact')
                  : t('manage-notifications.additional-point-of-contact')}
              </p>
              <Formik
                validateOnMount
                enableReinitialize
                validationSchema={Yup.object().shape({
                  email: Yup.string()
                    .required(t('manage-notifications.please-enter-email'))
                    .email(t('manage-notifications.email-is-invalid')),
                  phone: Yup.string()
                    .required(
                      t('manage-notifications.phone-number-is-required'),
                    )
                    .test(
                      'phone number',
                      t('manage-notifications.phone-number-is-invalid'),
                      (value) => isValidPhoneNumber(value ?? ''),
                    ),
                })}
                initialValues={{
                  email: '',
                  phone: '',
                }}
                onSubmit={(values) => {
                  onPointOfContactAdd(values);
                }}
              >
                {({ errors }) => {
                  return (
                    <Form className="d-flex gap-4 flex-column">
                      <FormikField
                        name={'email'}
                        required
                        component={InputField}
                        error={errors.email}
                        componentProps={{
                          placeholder: t(
                            'manage-notifications.enter-your-email-address-here',
                          ),
                          className: cn('w-100'),
                        }}
                      />

                      <FormikField
                        name={'phone'}
                        component={PhoneInputField}
                        required
                        error={errors.phone}
                        componentProps={{
                          placeholder: t(
                            'manage-notifications.enter-your-mobile-number-here',
                          ),
                        }}
                      />

                      <div className="d-flex gap-4">
                        <Button
                          onClick={() => {
                            setAddingContact(false);
                          }}
                          type="button"
                          className="px-5 flex-grow-1"
                          size="medium"
                          outlined
                          label={t('common.cancel')}
                        />
                        <Button
                          disabled={Object.keys(errors).length > 0}
                          type="submit"
                          className="px-5 flex-grow-1"
                          size="medium"
                          label={t('common.confirm')}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          )}
        </div>
        <Button
          label={t('manage-notifications.save')}
          disabled={!prefsUpdated || loading}
          className="saveButton"
          onClick={onSave}
        />
      </BlockUI>
    </MainLayout>
  );
};

export default React.memo(ManageNotifications);
